import React, { useState } from "react";
import { Card, Form, Button, Alert, Row, Col, Spinner } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  IconMail,
  IconCaretLeft,
  IconSend,
  IconMessage,
  IconBrandTelegram,
  IconBrandX,
  IconBrandFacebook,
  IconPhone,
  IconInfoCircle,
} from "@tabler/icons-react";

const MAX_SUBJECT = 200;
const MAX_MESSAGE = 10000;

const ContactUs = () => {
  const navigate = useNavigate();

  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const subjectLeft = Math.max(0, MAX_SUBJECT - subject.length);
  const messageLeft = Math.max(0, MAX_MESSAGE - message.length);

  const validate = () => {
    const s = subject.trim();
    const m = message.trim();
    if (s.length < 5) {
      setError("Please enter a subject (at least 5 characters).");
      return false;
    }
    if (m.length < 20) {
      setError("Please write a message (at least 20 characters).");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!validate()) return;

    try {
      setSending(true);
      const token = localStorage.getItem("token");
      await axios.post(
        "/api/contact-us",
        { subject, message },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSubmitted(true);
      setSubject("");
      setMessage("");
    } catch (err) {
      console.error("Failed to send message:", err);
      setError("Failed to send message. Please try again later.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact Us - IndexBooster</title>
      </Helmet>

      <div className="container-xl mt-3">
        <div className="d-flex align-items-center mb-3">
          <Button variant="light" size="sm" onClick={() => navigate(-1)} className="me-2">
            <IconCaretLeft size={18} />
          </Button>
          <div>
            <h2 className="m-0">Contact Us</h2>
            <div className="text-secondary">We're here to help — get in touch with our team.</div>
          </div>
        </div>

        <Row className="g-3 g-md-4">
          {/* Left column: Contact channels */}
          <Col lg={100}>
            <Card className="h-100">
              <div className="card-stamp">
                <div className="card-stamp-icon bg-primary">
                  <IconMail size={18} />
                </div>
              </div>
              <Card.Header>
                <Card.Title as="h3" className="m-0">Get in touch</Card.Title>
              </Card.Header>
              <Card.Body>
                <div className="mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <span className="avatar bg-primary-lt me-2">
                      <IconMail size={18} className="text-primary" />
                    </span>
                    <div>
                      <div className="text-secondary">Email</div>
                      <a href="mailto:support@indexbooster.net">support@indexbooster.net</a>
                    </div>
                  </div>
                  <div className="d-flex align-items-center mb-2">
                    <span className="avatar bg-info-lt me-2">
                      <IconBrandTelegram size={18} className="text-info" />
                    </span>
                    <div>
                      <div className="text-secondary">Telegram</div>
                      <a href="https://t.me/indexbooster" target="_blank" rel="noreferrer">@indexbooster</a>
                    </div>
                  </div>
                  <div className="d-flex align-items-center mb-2">
                    <span className="avatar bg-indigo-lt me-2">
                      <IconBrandX size={18} className="text-indigo" />
                    </span>
                    <div>
                      <div className="text-secondary">Twitter</div>
                      <a href="https://x.com/indexbooster" target="_blank" rel="noreferrer">@indexbooster</a>
                    </div>
                  </div>
                </div>

                <div className="alert alert-info d-flex align-items-start" role="alert">
                  <IconInfoCircle size={18} className="me-2 mt-1" />
                  <div>
                    We typically respond within 24 hours (Mon–Fri). For account or billing issues, use the email channel.
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Right column: Contact form */}
          <Col lg={100}>
            <Card className="h-100">
              <div className="card-stamp">
                <div className="card-stamp-icon bg-success">
                  <IconSend size={18} />
                </div>
              </div>
              <Card.Header>
                <Card.Title as="h3" className="m-0">Send us a message</Card.Title>
              </Card.Header>
              <Card.Body>
                {submitted ? (
                  <Alert variant="success">
                    ✅ Your message has been sent! We'll get back to you soon.
                  </Alert>
                ) : (
                  <Form onSubmit={handleSubmit} noValidate>
                    {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

                    <Form.Group className="mb-3">
                      <Form.Label className="d-flex align-items-center">
                        <IconMessage size={16} className="me-2" />
                        Subject
                      </Form.Label>
                      <Form.Control
                        type="text"
                        maxLength={MAX_SUBJECT}
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        placeholder="Briefly describe your question"
                        required
                      />
                      <div className="form-hint text-end">{subjectLeft} characters left</div>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label className="d-flex align-items-center">
                        <IconMessage size={16} className="me-2" />
                        Message
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={6}
                        maxLength={MAX_MESSAGE}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Tell us how we can help"
                        required
                      />
                      <div className="form-hint d-flex justify-content-between">
                        <span>Min 20 characters</span>
                        <span>{messageLeft} left</span>
                      </div>
                    </Form.Group>

                    <div className="d-flex align-items-center gap-2">
                      <Button type="submit" variant="primary" disabled={sending}>
                        {sending ? (<><Spinner size="sm" className="me-2" />Sending…</>) : "Send Message"}
                      </Button>
                      <Button
                        type="button"
                        variant="outline-secondary"
                        onClick={() => { setSubject(""); setMessage(""); setError(""); }}
                        disabled={sending}
                      >
                        Clear
                      </Button>
                    </div>

                    <div className="text-muted small mt-3">
                      We respect your privacy. Your details are used only to reply to your request.
                    </div>
                  </Form>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default ContactUs;
